import { useState } from "react";
import { eur } from "@/lib/api";
import { computeNotaryFees } from "@/lib/notaryFees";
import { Scale } from "lucide-react";

/**
 * NotaryFeesBlock — displays estimated acquisition fees (charged to the buyer).
 * Props:
 *  - price: number (property price EUR)
 *  - defaultKind: "ancien" | "neuf" (default "ancien")
 *  - variant: "light" | "dark" (default "light")
 */
export default function NotaryFeesBlock({ price, defaultKind = "ancien", variant = "light" }) {
  const [kind, setKind] = useState(defaultKind);
  const f = computeNotaryFees(price, kind);
  const dark = variant === "dark";
  return (
    <div data-testid="notary-fees" className={`${dark ? "bg-navy/40 border border-cream/15 text-cream" : "bg-cream/60 border border-navy/10 text-navy"} p-5`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <Scale size={18} className={dark ? "text-accent-brand" : "text-accent-brand"} />
          <div>
            <div className={`text-[10px] uppercase tracking-widest font-bold ${dark ? "text-accent-brand" : "text-accent-brand"}`}>Coût d'acquisition estimé</div>
            <div className={`text-xs ${dark ? "text-cream/60" : "text-navy/60"}`}>Frais de notaire à la charge de l'acquéreur</div>
          </div>
        </div>
        <div className={`inline-flex text-[10px] font-bold uppercase tracking-wider ${dark ? "bg-cream/10" : "bg-white border border-navy/10"}`}>
          <button type="button" data-testid="notary-kind-ancien" onClick={() => setKind("ancien")}
            className={`px-3 py-1.5 ${kind === "ancien" ? "bg-accent-brand text-white" : dark ? "text-cream/70" : "text-navy/60"}`}>
            Ancien
          </button>
          <button type="button" data-testid="notary-kind-neuf" onClick={() => setKind("neuf")}
            className={`px-3 py-1.5 ${kind === "neuf" ? "bg-accent-brand text-white" : dark ? "text-cream/70" : "text-navy/60"}`}>
            Neuf / VEFA
          </button>
        </div>
      </div>

      <div className={`font-serif text-3xl md:text-4xl font-semibold ${dark ? "text-accent-brand" : "text-accent-brand"}`} data-testid="notary-total">
        {eur(f.total)}
      </div>
      <div className={`text-xs mb-4 ${dark ? "text-cream/60" : "text-navy/60"}`}>
        soit environ <strong>{(f.pct * 100).toFixed(2).replace(".", ",")} %</strong> du prix
      </div>

      <ul className={`text-xs space-y-1 border-t ${dark ? "border-cream/10" : "border-navy/10"} pt-3`}>
        <li className="flex justify-between"><span>Droits de mutation (DMTO {kind === "neuf" ? "réduit 0,715 %" : "5,80 %"})</span><span className="font-mono">{eur(f.dmto)}</span></li>
        <li className="flex justify-between"><span>Émoluments notaire (barème progressif, TTC)</span><span className="font-mono">{eur(f.emolumentsTTC)}</span></li>
        <li className="flex justify-between"><span>Contribution sécurité immobilière (0,10 %)</span><span className="font-mono">{eur(f.csi)}</span></li>
        <li className="flex justify-between"><span>Débours forfaitaires</span><span className="font-mono">{eur(f.debours)}</span></li>
      </ul>
      <div className={`text-[11px] italic mt-3 ${dark ? "text-cream/50" : "text-navy/50"}`}>
        Estimation indicative, à confirmer par le notaire. TVA 20 % appliquée sur les émoluments.
      </div>
    </div>
  );
}
